import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { getOcppActiveSocketsForSiteOptions } from "../../../services/client/@tanstack/react-query.gen";
import type { OcppConfiguration } from "../../../services/client/types.gen";

type ForwardUrlOverridesFieldProps = {
  site_id: string;
  configured_socket_ids?: OcppConfiguration["socket_to_asset_map"];
};

export const ForwardUrlOverridesField: React.FC<
  ForwardUrlOverridesFieldProps
> = ({ site_id, configured_socket_ids }) => {
  const { data: activeSockets } = useQuery({
    ...getOcppActiveSocketsForSiteOptions({ path: { site_id } }),
  });

  const socketIdOptions = useMemo(() => {
    const ids = new Set<string>();
    activeSockets?.forEach((s) => ids.add(s.id));
    if (configured_socket_ids) {
      Object.keys(configured_socket_ids).forEach((id) => ids.add(id));
    }
    return Array.from(ids)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((id) => ({ value: id, label: id }));
  }, [activeSockets, configured_socket_ids]);

  return (
    <div className="mt-4">
      <Typography.Title level={5} className="!mb-1">
        Forward URL Overrides
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="text-xs !mb-3">
        Forward messages from specific OCPP socket IDs to a different CPMS URL.
        Pick socket IDs we've seen, or type a custom one and press Enter.
      </Typography.Paragraph>

      <Form.List name="forward_url_overrides">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <Space
                key={field.key}
                align="baseline"
                className="flex w-full mb-2"
              >
                <Form.Item
                  {...field}
                  name={[field.name, "forward_url"]}
                  rules={[
                    { required: true, message: "Forward URL is required" },
                    { type: "url", message: "Enter a valid URL" },
                  ]}
                  className="!mb-0 flex-1"
                >
                  <Input
                    placeholder="wss://example.com/ocpp"
                    style={{ width: 360 }}
                  />
                </Form.Item>
                <Form.Item
                  {...field}
                  name={[field.name, "socket_ids"]}
                  rules={[
                    {
                      required: true,
                      message: "At least one socket ID is required",
                      type: "array",
                      min: 1,
                    },
                  ]}
                  className="!mb-0"
                >
                  <Select
                    mode="tags"
                    placeholder="Socket IDs"
                    options={socketIdOptions}
                    style={{ minWidth: 320 }}
                    tokenSeparators={[",", " "]}
                  />
                </Form.Item>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(field.name)}
                />
              </Space>
            ))}
            <Form.Item className="!mb-0">
              <Button
                type="dashed"
                onClick={() => add({ forward_url: "", socket_ids: [] })}
                icon={<PlusOutlined />}
              >
                Add override
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );
};
